import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';

import Svg from '@svg/index';
import { Flex } from '@ui-library/flex';
import { Tooltip } from '@ui-library/tooltip';
import { InputField } from '@ui-library/input';
import { ModalStep } from '@components/common/multiStepModal';
import { ContextMenuV2 } from '@components/common/contextMenu';
import { useMultiStepModalStore } from '@components/common/multiStepModal/modalStore';
import { canCreateDestinationAccount } from '@hooks/useActionGuard/utils/permissionChecks';
import { LargeText } from '@ui-library/typography';

import { useDesignVariant } from '../../designVariantContext';
import { useAccountManagementStore } from '../../store';
import { useDeleteAccountPermissions } from './useDeleteAccountPermissions';
import { useEditAccountPermissions } from './useEditAccountPermissions';
import { AccountDetails } from '../accountDetails';
import { SelectableCard } from '../../styles';
import { CurrentAccountBadge } from '../currentAccountBadge';
import { getMetadataSearchString } from '../utils';

interface AccountPickerProps {
  initialAccountId?: string;
  selectedAccountId?: string;
}

export const AccountPickerModalStep: React.FC<AccountPickerProps> = observer(
  ({ initialAccountId, selectedAccountId }) => {
    const accountManagementStore = useAccountManagementStore();
    const multiStepModalStore = useMultiStepModalStore();
    const {
      resourceType,
      compatibleAccounts,
      compatibleAccountDefinitions,
      setAccountId,
      setDeleteAccountId,
      setEditAccountId,
      isAccountOAuth,
    } = accountManagementStore;

    const variant = useDesignVariant();
    const isNext = variant === 'next';

    const [newAccountId, setNewAccountId] = useState<string>(
      selectedAccountId ?? initialAccountId ?? '',
    );
    const [searchText, setSearchText] = useState<string>('');
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const sortedCompatibleAccounts = React.useMemo(() => {
      const [current, others] = partition(compatibleAccounts, (a) => a.id === initialAccountId);
      const sortedOthers = orderBy(others, ['updatedAt'], ['desc']);
      return current.length > 0 ? [...current, ...sortedOthers] : sortedOthers;
    }, [compatibleAccounts, initialAccountId]);

    const filteredCompatibleAccounts = React.useMemo(() => {
      if (!searchText) {
        return sortedCompatibleAccounts;
      }

      return sortedCompatibleAccounts.filter((account) => {
        const accountDefinition = compatibleAccountDefinitions.find(
          (def) => def.name === account.accountDefinitionName,
        );

        const nameMatch = account.name.toLowerCase().includes(searchText.toLowerCase());
        const authTypeMatch = accountDefinition?.authenticationType
          ?.toLowerCase()
          .includes(searchText.toLowerCase());
        const metadataString = getMetadataSearchString(account.metadata);
        const metadataMatch = metadataString.toLowerCase().includes(searchText.toLowerCase());

        return nameMatch || authTypeMatch || metadataMatch;
      });
    }, [sortedCompatibleAccounts, searchText, compatibleAccountDefinitions]);

    const { deletePermissionsMap } = useDeleteAccountPermissions(
      filteredCompatibleAccounts,
      resourceType,
    );

    const { editPermissionsMap } = useEditAccountPermissions(
      filteredCompatibleAccounts,
      resourceType,
    );

    const handleDeleteClick = (accountId: string) => {
      setDeleteAccountId(accountId);
      multiStepModalStore.navigate('deleteAccountConfirmation');
    };

    const handleEditClick = (accountId: string) => {
      setEditAccountId(accountId);
      multiStepModalStore.navigate('editAccountForm');
    };

    const createDestinationAccountPermission = canCreateDestinationAccount();

    const noSearchResults = searchText.trim() !== '' && filteredCompatibleAccounts.length === 0;

    return (
      <ModalStep
        gap="16px"
        disabled={newAccountId === '' || newAccountId === initialAccountId}
        header={{
          title: 'Choose from the list of existing accounts',
          description: 'Current denotes the account you are currently using',
        }}
        footer={{
          backButton: {
            visible: true,
            text: 'Add account',
            type: 'secondary',
            size: 'middle',
            icon: !isNext && <FontAwesomeIcon icon={regular('plus')} />,
            onClick: () => multiStepModalStore.navigate('accountTypePicker'),
            disabled: !createDestinationAccountPermission.isAllowed,
            tooltip: { title: createDestinationAccountPermission.denialReason },
          },
          actionButton: {
            visible: true,
            text: 'Use account',
            onClick: () => setAccountId(newAccountId),
          },
        }}
      >
        <InputField
          variant={variant}
          placeholder="Search by name, metadata etc"
          value={searchText}
          prefix={<FontAwesomeIcon icon={regular('search')} size="sm" />}
          onChange={(e) => setSearchText(e.target.value)}
          className="m-b-md"
        />
        <Flex
          ref={scrollContainerRef}
          flexGrow
          flexDirection="column"
          gap=".5rem"
          style={{ overflowY: 'auto', height: '50vh', padding: '2px' }}
        >
          {noSearchResults ? (
            <Flex flexDirection="column" flexGrow centered>
              <Svg name="empty_box" size={200} />
              <LargeText>No accounts found</LargeText>
            </Flex>
          ) : (
            filteredCompatibleAccounts.map((account) => (
              <SelectableCard
                key={account.id}
                isSelected={newAccountId === account.id}
                variant={variant}
                onClick={() => setNewAccountId(account.id)}
              >
                <Flex flexGrow flexDirection="column" justifyContent="space-between" gap="16px">
                  <Flex flexDirection="row" justifyContent="space-between" alignItemsCenter>
                    <AccountDetails store={accountManagementStore} accountId={account.id} />
                    {initialAccountId === account.id ? (
                      <Flex gap="8px" alignItemsCenter>
                        <CurrentAccountBadge />
                        {!isAccountOAuth(account.id) && (
                          <ContextMenuV2
                            variant={variant}
                            scrollContainerRef={scrollContainerRef}
                            items={[
                              {
                                key: 'edit-account',
                                label: (
                                  <Tooltip title={editPermissionsMap[account.id].denialReason}>
                                    Edit account
                                  </Tooltip>
                                ),
                                icon: <FontAwesomeIcon icon={regular('pen-to-square')} />,
                                onClick: () => handleEditClick(account.id),
                                disabled: !editPermissionsMap[account.id].isAllowed,
                              },
                            ]}
                            size={isNext ? 'middle' : 'small'}
                          />
                        )}
                      </Flex>
                    ) : (
                      <ContextMenuV2
                        variant={variant}
                        scrollContainerRef={scrollContainerRef}
                        items={[
                          ...(!isAccountOAuth(account.id)
                            ? [
                                {
                                  key: 'edit-account',
                                  label: (
                                    <Tooltip title={editPermissionsMap[account.id].denialReason}>
                                      Edit account
                                    </Tooltip>
                                  ),
                                  icon: <FontAwesomeIcon icon={regular('pen-to-square')} />,
                                  onClick: () => handleEditClick(account.id),
                                  disabled: !editPermissionsMap[account.id].isAllowed,
                                },
                              ]
                            : []),
                          {
                            key: 'delete-account',
                            label: (
                              <Tooltip title={deletePermissionsMap[account.id].denialReason}>
                                Delete account
                              </Tooltip>
                            ),
                            danger: true,
                            icon: <FontAwesomeIcon icon={regular('trash')} />,
                            onClick: () => handleDeleteClick(account.id),
                            disabled: !deletePermissionsMap[account.id].isAllowed,
                          },
                        ]}
                        size={isNext ? 'middle' : 'small'}
                      />
                    )}
                  </Flex>
                </Flex>
              </SelectableCard>
            ))
          )}
        </Flex>
      </ModalStep>
    );
  },
);
